import React, { memo } from "react";

// Signal table (DESIGN.md "Table"): sticky micro-mono header, 40 px rows,
// hairline dividers, hover raise. Styles live in styles/signal.css (.sg-table).
// This is the single implementation; ./Table.jsx re-exports it.

interface TableProps {
  children: React.ReactNode;
  className?: string;
  striped?: boolean;
  compact?: boolean;
  bordered?: boolean;
  stickyHeader?: boolean;
  mobileCards?: boolean;
  caption?: string;
  ariaLabel?: string;
  [key: string]: any;
}

interface TableHeadProps {
  children: React.ReactNode;
  className?: string;
  sticky?: boolean;
  [key: string]: any;
}

interface TableBodyProps {
  children: React.ReactNode;
  className?: string;
  striped?: boolean;
  [key: string]: any;
}

interface TableRowProps {
  children: React.ReactNode;
  isHeader?: boolean;
  isHoverable?: boolean;
  isSelected?: boolean;
  className?: string;
  onClick?: (event?: any) => void;
  [key: string]: any;
}

interface TableCellProps {
  children?: React.ReactNode;
  isHeader?: boolean;
  className?: string;
  align?: "left" | "center" | "right";
  nowrap?: boolean;
  truncate?: boolean;
  width?: string | number;
  dataLabel?: string;
  [key: string]: any;
}

interface TableEmptyProps {
  message?: string;
  icon?: React.ReactNode;
  actionText?: string;
  onAction?: () => void;
  colSpan?: number;
  className?: string;
  [key: string]: any;
}

interface TableLoadingProps {
  message?: string;
  colSpan?: number;
  className?: string;
  [key: string]: any;
}

interface TablePaginationProps {
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  totalItems?: number;
  itemsPerPage?: number;
  showInfo?: boolean;
  className?: string;
  [key: string]: any;
}

const ALIGN: Record<string, string> = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
};

const cx = (...parts: Array<string | false | null | undefined>) =>
  parts.filter(Boolean).join(" ");

const Table: React.FC<TableProps> & {
  Head: React.FC<TableHeadProps>;
  Body: React.FC<TableBodyProps>;
  Row: React.FC<TableRowProps>;
  Cell: React.FC<TableCellProps>;
  Empty: React.FC<TableEmptyProps>;
  Loading: React.FC<TableLoadingProps>;
  Pagination: React.FC<TablePaginationProps>;
} = ({
  children,
  className = "",
  striped = false,
  compact = false,
  bordered = false,
  stickyHeader = false,
  mobileCards = true,
  caption = "",
  ariaLabel,
  ...props
}) => (
  <div className="sg-table-wrap" data-bordered={bordered || undefined}>
    <table
      className={cx("sg-table", className)}
      data-density={compact ? "compact" : undefined}
      data-stack={mobileCards ? "true" : undefined}
      data-striped={striped || undefined}
      data-sticky={stickyHeader || undefined}
      aria-label={ariaLabel || undefined}
      {...props}
    >
      {caption && <caption className="sr-only">{caption}</caption>}
      {children}
    </table>
  </div>
);

const TableHead: React.FC<TableHeadProps> = memo(
  ({ children, className = "", sticky = false, ...props }) => (
    <thead className={className || undefined} {...props}>
      {children}
    </thead>
  ),
);
TableHead.displayName = "TableHead";

const TableBody: React.FC<TableBodyProps> = memo(
  ({ children, className = "", striped = false, ...props }) => (
    <tbody
      className={className || undefined}
      data-striped={striped || undefined}
      {...props}
    >
      {children}
    </tbody>
  ),
);
TableBody.displayName = "TableBody";

const TableRow: React.FC<TableRowProps> = memo(
  ({
    children,
    isHeader = false,
    isHoverable = true,
    isSelected = false,
    className = "",
    onClick,
    ...props
  }) => {
    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (onClick && (e.key === "Enter" || e.key === " ")) {
        e.preventDefault();
        onClick(e);
      }
    };

    return (
      <tr
        className={cx(onClick && "cursor-pointer", className) || undefined}
        data-static={!isHeader && !isHoverable ? "true" : undefined}
        onClick={onClick}
        onKeyDown={onClick ? handleKeyDown : undefined}
        role={onClick ? "button" : undefined}
        tabIndex={onClick ? 0 : undefined}
        aria-selected={isSelected ? true : undefined}
        {...props}
      >
        {children}
      </tr>
    );
  },
);
TableRow.displayName = "TableRow";

const TableCell: React.FC<TableCellProps> = memo(
  ({
    children,
    isHeader = false,
    className = "",
    align = "left",
    nowrap = false,
    truncate = false,
    width,
    dataLabel,
    ...props
  }) => {
    const classes = cx(
      ALIGN[align] || ALIGN.left,
      nowrap && "whitespace-nowrap",
      truncate && "truncate",
      className,
    );
    const style = width ? { width } : undefined;

    if (isHeader) {
      return (
        <th scope="col" style={style} className={classes} {...props}>
          {children}
        </th>
      );
    }

    return (
      <td
        style={style}
        className={classes}
        {...(typeof dataLabel !== "undefined" ? { "data-label": dataLabel } : {})}
        {...props}
      >
        {children}
      </td>
    );
  },
);
TableCell.displayName = "TableCell";

const TableEmpty: React.FC<TableEmptyProps> = memo(
  ({
    message = "No data available",
    icon,
    actionText,
    onAction,
    colSpan = 1,
    className = "",
    ...props
  }) => (
    <tr className={className || undefined} {...props}>
      <td colSpan={colSpan} className="!py-12 text-center">
        <div
          className="flex flex-col items-center justify-center gap-3"
          role="status"
          aria-live="polite"
        >
          {icon && <div className="text-sg-ink-3">{icon}</div>}
          <p className="text-sm text-sg-ink-2">{message}</p>
          {actionText && onAction && (
            <button
              className="sg-btn sg-btn-secondary sg-btn-sm"
              onClick={onAction}
              type="button"
            >
              {actionText}
            </button>
          )}
        </div>
      </td>
    </tr>
  ),
);
TableEmpty.displayName = "TableEmpty";

const TableLoading: React.FC<TableLoadingProps> = memo(
  ({ message = "Loading data...", colSpan = 1, className = "", ...props }) => (
    <tr className={className || undefined} {...props}>
      <td
        colSpan={colSpan}
        className="!py-12 text-center"
        aria-busy="true"
        aria-live="polite"
      >
        <div className="flex items-center justify-center gap-3 text-sm text-sg-ink-2">
          <span className="sg-btn-spinner" aria-hidden="true" />
          <span>{message}</span>
        </div>
      </td>
    </tr>
  ),
);
TableLoading.displayName = "TableLoading";

const Chevron = ({ d }: { d: string }) => (
  <svg
    className="h-4 w-4"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    aria-hidden="true"
  >
    <path fillRule="evenodd" d={d} clipRule="evenodd" />
  </svg>
);

const FIRST =
  "M15.707 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 010 1.414zm-6 0a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 011.414 1.414L5.414 10l4.293 4.293a1 1 0 010 1.414z";
const PREV =
  "M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z";
const NEXT =
  "M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z";
const LAST =
  "M4.293 15.707a1 1 0 001.414 0l5-5a1 1 0 000-1.414l-5-5a1 1 0 00-1.414 1.414L8.586 10 4.293 14.293a1 1 0 000 1.414zm6 0a1 1 0 001.414 0l5-5a1 1 0 000-1.414l-5-5a1 1 0 00-1.414 1.414L14.586 10l-4.293 4.293a1 1 0 000 1.414z";

const TablePagination: React.FC<TablePaginationProps> = memo(
  ({
    currentPage = 1,
    totalPages = 1,
    onPageChange,
    totalItems,
    itemsPerPage,
    showInfo = true,
    className = "",
    ...props
  }) => {
    const isFirstPage = currentPage === 1;
    const isLastPage = currentPage === totalPages;

    const handlePageChange = (page: number) => {
      if (page >= 1 && page <= totalPages && onPageChange) {
        onPageChange(page);
      }
    };

    const maxButtons = Math.min(5, totalPages);
    let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    let endPage = startPage + maxButtons - 1;
    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - maxButtons + 1);
    }
    const pageNumbers: number[] = [];
    for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);

    const startItem = totalItems
      ? (currentPage - 1) * (itemsPerPage || 0) + 1
      : 0;
    const endItem = totalItems
      ? Math.min(startItem + (itemsPerPage || 0) - 1, totalItems)
      : 0;

    const iconBtn = "sg-btn sg-btn-ghost sg-btn-sm sg-btn-icon";

    return (
      <nav
        className={cx(
          "flex flex-col items-center justify-between gap-3 py-3 sm:flex-row",
          className,
        )}
        aria-label="Pagination"
        {...props}
      >
        {showInfo && totalItems ? (
          <div className="font-mono text-xs tabular-nums text-sg-ink-3">
            Showing <span className="text-sg-ink">{startItem}</span> to{" "}
            <span className="text-sg-ink">{endItem}</span> of{" "}
            <span className="text-sg-ink">{totalItems}</span> results
          </div>
        ) : (
          <span />
        )}

        <div className="inline-flex items-center gap-1" role="group">
          <button
            className={iconBtn}
            onClick={() => handlePageChange(1)}
            disabled={isFirstPage}
            aria-label="First page"
            type="button"
          >
            <Chevron d={FIRST} />
          </button>
          <button
            className={iconBtn}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={isFirstPage}
            aria-label="Previous page"
            type="button"
          >
            <Chevron d={PREV} />
          </button>
          {pageNumbers.map((page) => (
            <button
              key={page}
              className="sg-btn sg-btn-ghost sg-btn-sm min-w-[28px] px-2 font-mono tabular-nums"
              data-active={page === currentPage ? "true" : undefined}
              onClick={() => handlePageChange(page)}
              aria-current={page === currentPage ? "page" : undefined}
              aria-label={`Page ${page}`}
              type="button"
            >
              {page}
            </button>
          ))}
          <button
            className={iconBtn}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={isLastPage}
            aria-label="Next page"
            type="button"
          >
            <Chevron d={NEXT} />
          </button>
          <button
            className={iconBtn}
            onClick={() => handlePageChange(totalPages)}
            disabled={isLastPage}
            aria-label="Last page"
            type="button"
          >
            <Chevron d={LAST} />
          </button>
        </div>
      </nav>
    );
  },
);
TablePagination.displayName = "TablePagination";

Table.Head = TableHead;
Table.Body = TableBody;
Table.Row = TableRow;
Table.Cell = TableCell;
Table.Empty = TableEmpty;
Table.Loading = TableLoading;
Table.Pagination = TablePagination;

export default Table;
